import { Input } from './input.mjs';
import { registerCustomElement } from '../utils.mjs';
import EVENTS from '../events.mjs';
import EventManager from '../eventManager.mjs';

export class GRecaptcha extends Input {
	constructor() {
		super();
		this._grecaptchav2widget = null;
		this._grecaptchaDefaultVersion = 3;
		this._grecaptchaAllowedVersions = [2, 3];
	}

	/**
	 * Builds the input dom.
	 *
	 * @param instructions {object} Collection of directives.
	 * @param value {any} Initial value.
	 * @param metadata {object} Metadata.
	 */

	async _build(instructions, value, metadata) {
		const siteKey = instructions?.options?.siteKey;

		if (!siteKey) {
			return;
		}
		let version = instructions?.options?.version;

		if (!this._grecaptchaAllowedVersions.includes(version)) {
			version = this._grecaptchaDefaultVersion;
		}

		this.setAttribute('name', instructions.name || `grecaptchav${version}`);
		this.dataset.version = version;
		this.className = 'form-group';

		if (instructions?.options?.hidden) {
			this.style.display = 'none';
		}

		this.loadScript(siteKey);

		this.form.addEventListener(EVENTS.FORM.G_RECAPTCHA_LOADED, async () => {
			switch (version) {
				case 2:
					this.injectV2Widget(siteKey);
					break;
				case 3:
					this._value = await this.getRecaptchaToken(siteKey);
					break;
			}
		});

		this.form.addEventListener(EVENTS.FORM.ERROR, async (event) => {
			const error = event.detail;

			switch (version) {
				case 2:
					if (error.code === 'IM020' && typeof window.grecaptcha !== 'undefined' && this._grecaptchav2widget !== null) {
						window.grecaptcha.reset(this._grecaptchav2widget);
					}
					break;
				case 3:
					const v2fallback = this.form.querySelector('imt-input-grecaptcha[data-version="2"]');

					if (error.code === 'IM021' && v2fallback) {
						v2fallback.style.display = 'block';
					}
					break;
			}
		});
	}

	loadScript(siteKey) {
		if (this.form._grecaptchaScriptLoaded) {
			return;
		}
		this.form._grecaptchaScriptLoaded = true;

		const script = document.createElement('script');

		script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
		script.async = true;
		this.appendChild(script);

		script.onload = async () => {
			EventManager.dispatch(this.form, EVENTS.FORM.G_RECAPTCHA_LOADED, { bubbles: true, detail: {} });
		};
	}

	injectV2Widget(siteKey) {
		if (typeof window.grecaptcha === 'undefined' || !siteKey) {
			return;
		}

		const container = document.createElement('div');

		container.id = 'g-recaptcha-v2-container';
		this.appendChild(container);

		const _setValue = (response) => {
			if (response) {
				this._value = response;
			}
		};

		window.grecaptcha.ready(() => {
			this._grecaptchav2widget = window.grecaptcha.render(container, {
				sitekey: siteKey,
				callback: _setValue,
			});
		});
	}

	getRecaptchaToken(siteKey) {
		return new Promise((resolve, reject) => {
			if (typeof window.grecaptcha === 'undefined' || !siteKey) {
				return resolve(null);
			}

			try {
				window.grecaptcha.ready(() => {
					window.grecaptcha.execute(siteKey, { action: 'submit' }).then((token) => {
						resolve(token);
					});
				});
				// eslint-disable-next-line no-unused-vars
			} catch (e) {
				resolve(null);
			}
		});
	}

	get hasModeSwitch() {
		return false;
	}

	get value() {
		return this._value;
	}

	set value(value) {
		if (this._value === value) return;
		this._value = value;
	}
}

registerCustomElement('imt-input-grecaptcha', GRecaptcha);
